// ---------------------------------------------------------------------------
// Post-checkout confirmation.
//
// The order arrives via router state straight after checkout; on a refresh or a
// shared link it is fetched again using the order number plus the phone the
// customer used, so the page is never a bare "thanks" with no details.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { api, ApiClientError } from '../lib/apiClient.js';
import { useSite } from '../context/SiteProvider.jsx';
import { Reveal } from '../components/common/Reveal.jsx';
import { RichText } from '../components/common/RichText.jsx';
import { formatDate } from '../lib/format.js';
import { OrderSummaryCard, StatusBadge } from '../components/site/OrderSummary.jsx';

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { t, language } = useSite();

  const passed = location.state?.order ?? null;
  const customer = location.state?.customer ?? null;

  const [order, setOrder] = useState(null);
  const [phone, setPhone] = useState(customer?.phone ?? '');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch the full order (line items, timeline) as soon as we have a phone.
  useEffect(() => {
    if (!customer?.phone) return;
    setIsLoading(true);
    api
      .post('/orders/track', { orderNumber, phone: customer.phone })
      .then((result) => setOrder(result.order))
      .catch(() => setError('lookup'))
      .finally(() => setIsLoading(false));
  }, [orderNumber, customer?.phone]);

  const lookup = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.post('/orders/track', { orderNumber, phone });
      setOrder(result.order);
    } catch (cause) {
      setError(cause instanceof ApiClientError && cause.code === 'ORDER_NOT_FOUND' ? 'notFound' : 'generic');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main">
      <section className="order-page section-pad">
        <Reveal className="order-intro">
          <p className="eyebrow">{t('orderPlacedEyebrow')}</p>
          <RichText as="h2" html={t('orderPlacedTitle')} />
          <p>{t('orderPlacedText')}</p>

          <div className="order-number-card">
            <span>{t('orderNumberLabel')}</span>
            <strong>{orderNumber}</strong>
            <small>{t('saveOrderNumber')}</small>
          </div>

          <div className="payment-notice">
            <span>✓</span>
            <div>
              <strong>{t('cashOnDelivery')}</strong>
              <small>{t('payWhenDelivered')}</small>
            </div>
          </div>

          <div className="hero-buttons">
            <Link className="button dark" to="/shop">
              <span>{t('continueShopping')}</span>
              <span>↗</span>
            </Link>
          </div>
        </Reveal>

        <Reveal className="order-detail">
          {order ? (
            <>
              <div className="order-status-row">
                <div>
                  <span>{t('orderStatus')}</span>
                  <StatusBadge status={order.status} />
                </div>
                <div>
                  <span>{t('orderDate')}</span>
                  <b>{formatDate(order.placedAt, language)}</b>
                </div>
              </div>
              <OrderSummaryCard order={order} />
            </>
          ) : (
            <form className="order-lookup" onSubmit={lookup}>
              <h3>{t('orderSummary')}</h3>
              <label>
                <span>{t('trackPhoneLabel')}</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  required
                />
              </label>
              {error ? <em className="field-error">{t(error === 'notFound' ? 'trackNotFound' : 'errorGeneric')}</em> : null}
              <button className="button dark" type="submit" disabled={isLoading}>
                <span>{isLoading ? t('loading') : t('trackButton')}</span>
                <span>↗</span>
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </main>
  );
}
